/**
 * Debug-only console ring buffer. Browser DevTools console is not readable
 * from page JS — we mirror console.* into memory so a button can copy it.
 */

const MAX_CHARS = 2_000_000;
const MAX_LINES = 8_000;

type ConsoleMethod = 'log' | 'info' | 'warn' | 'error' | 'debug';

let installed = false;
const lines: string[] = [];
let totalChars = 0;

function serializeArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
  }
  try {
    return JSON.stringify(arg, (_k, v) => {
      if (typeof v === 'bigint') return String(v);
      if (v instanceof MediaStream) {
        return `[MediaStream tracks=${v.getTracks().length}]`;
      }
      if (v instanceof MediaStreamTrack) {
        return `[MediaStreamTrack ${v.kind} ${v.readyState} enabled=${v.enabled}]`;
      }
      return v;
    }, 2);
  } catch {
    try {
      return String(arg);
    } catch {
      return '[unserializable]';
    }
  }
}

function pushLine(level: ConsoleMethod, args: unknown[]): void {
  const ts = new Date().toISOString();
  const body = args.map(serializeArg).join(' ');
  const line = `[${ts}] ${level.toUpperCase()} ${body}`;
  lines.push(line);
  totalChars += line.length + 1;
  while (lines.length > MAX_LINES || totalChars > MAX_CHARS) {
    const dropped = lines.shift();
    if (!dropped) break;
    totalChars -= dropped.length + 1;
  }
}

/** Install once, as early as possible (main.ts). */
export function installConsoleCapture(): void {
  if (installed || typeof console === 'undefined') return;
  installed = true;

  const methods: ConsoleMethod[] = ['log', 'info', 'warn', 'error', 'debug'];
  for (const method of methods) {
    const original = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      try {
        pushLine(method, args);
      } catch {
        /* never break logging */
      }
      original(...args);
    };
  }

  try {
    (window as any).__consoleCapture = {
      getText: getCapturedConsoleText,
      clear: clearCapturedConsole,
      copy: copyCapturedConsoleToClipboard,
      count: () => lines.length,
    };
  } catch {
    /* ignore */
  }
}

/**
 * Version from the loaded ai-chat widget script (ai-chat-vX.Y.Z.js).
 * Returns e.g. "0.0.185", or null if not found.
 */
export function resolveAiChatWidgetVersion(): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const re = /ai-chat-v(\d+\.\d+\.\d+)\.js/i;
    const scripts = document.querySelectorAll('script[src]');
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].getAttribute('src') || '';
      const m = src.match(re);
      if (m?.[1]) return m[1];
    }
    // Angular may inject via link/preload in some builds — also scan HTML.
    const html = document.documentElement?.innerHTML || '';
    const m2 = html.match(re);
    if (m2?.[1]) return m2[1];
  } catch {
    /* ignore */
  }
  return null;
}

/** Label suffix for the debug button, e.g. "v0.0.185" or "v?". */
export function aiChatWidgetVersionLabel(): string {
  const v = resolveAiChatWidgetVersion();
  return v ? `v${v}` : 'v?';
}

export function getCapturedConsoleText(): string {
  return lines.join('\n');
}

export function clearCapturedConsole(): void {
  lines.length = 0;
  totalChars = 0;
}

export async function copyCapturedConsoleToClipboard(): Promise<boolean> {
  const body = getCapturedConsoleText();
  if (!body) return false;
  const ver = aiChatWidgetVersionLabel();
  const text = `ai-chat ${ver}\n${body}`;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
